import FooterLocale from 'locale/en/footer.json';

/**
 * Extended footer disclaimer: any page can supply extra disclaimer lines that
 * render above the standard FDA statement in the site footer. Each line may begin
 * with a footnote marker (e.g. *, †, ††, ‡, §) that renders as superscript.
 *
 * PDPs source their lines from the Shopify metafield
 * `custom.extended_footer_disclaimer_lines` (a JSON string array); other pages
 * (e.g. the member journey) supply hardcoded lines directly.
 */
export const EXTENDED_FOOTER_DISCLAIMER_METAFIELD_KEY =
  'extended_footer_disclaimer_lines';

/**
 * To add a page-specific extended footer disclaimer, pass a FooterDisclaimerConfig into
 * withLayoutSelector through pageProps. The page's layout component should then
 * pass that config into FooterNavigation, which will render the lines above the
 * FDA statement in the site footer. reference JourneyLayout.tsx:
 *
 *   withLayoutSelector(page, {
 *     ...pageProps,
 *     footerDisclaimerConfig: { lines: ['†your disclaimer here'] },
 *   });
 *
 * Each line shows above the FDA statement in the site footer. Start a line with
 * a footnote marker (* † ‡ § ¶ ° Δ) and it gets superscripted for you.
 * PDPs are the odd one out, theirs come from PDP data or a Shopify metafield, see
 * resolvePdpFooterDisclaimerLines and resolvePdpFooterDisclaimerConfig below.
 */
export type FooterDisclaimerConfig = {
  lines: string[];
  /**
   * Replaces the default FDA statement (FooterLocale.these_statements) that renders
   * as the footer's primary line. Set this when a page needs a different marker or
   * wording on that line, so it isn't duplicated as one of `lines`.
   */
  primaryStatementOverride?: string;
  /** Renders all of `lines` in the primary disclaimer box instead of the secondary wrapper above it. */
  renderLinesAsPrimary?: boolean;
  /** `top` shows the disclaimer above the Seed logo instead of after the newsletter / nav. */
  placement?: 'top';
};

// Footnote-reference symbols that may prefix a disclaimer line and render as
// superscript (*, †, ††, ‡, §, ¶, °, Δ). Add new markers to this class as they're introduced.
const LEADING_FOOTNOTE_MARKER = /^([*†‡§¶°Δ]+)/;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

/**
 * Turn a plain-text disclaimer line into footer markup: escape it, then wrap its
 * leading footnote marker (if any) in `<sup>…</sup>`. The footer prints the result
 * with dangerouslySetInnerHTML, and PDP lines come from a Shopify metafield an
 * author controls, so lines are always treated as text and never as markup.
 */
export const footnoteMarkersToSupHtml = (value: string): string =>
  escapeHtml(value).replace(
    LEADING_FOOTNOTE_MARKER,
    (marker) => `<sup>${marker}</sup>`,
  );

export const parseExtendedFooterLinesFromMetafield = (
  value: string | null | undefined,
): string[] | null => {
  if (value == null || !String(value).trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!Array.isArray(parsed)) {
      return null;
    }
    return parsed.map((item) => (typeof item === 'string' ? item.trim() : ''));
  } catch {
    return null;
  }
};

const defaultExtendedLine0 = () =>
  `††${FooterLocale.ingredient_clinical_research}`;
const defaultExtendedLine1 = () => `**${FooterLocale.mild_bothersome_bloating}`;

// Trims each line and drops empty or non-string ones; the 2 locale lines stand in when none are left and fallback is on.
export const resolvePdpFooterDisclaimerLines = (
  lines: readonly unknown[] | null | undefined,
  fallbackExtendedFromLocaleWhenEmpty: boolean,
): FooterDisclaimerConfig | undefined => {
  let resolved = (lines ?? [])
    .map((line) => (typeof line === 'string' ? line.trim() : ''))
    .filter((line) => line.length > 0);
  if (resolved.length === 0 && fallbackExtendedFromLocaleWhenEmpty) {
    resolved = [defaultExtendedLine0(), defaultExtendedLine1()];
  }
  return resolved.length > 0 ? { lines: resolved } : undefined;
};

export const resolvePdpFooterDisclaimerConfig = (options: {
  metafieldValue: string | null | undefined;
  fallbackExtendedFromLocaleWhenEmpty: boolean;
}): FooterDisclaimerConfig | undefined =>
  resolvePdpFooterDisclaimerLines(
    parseExtendedFooterLinesFromMetafield(options.metafieldValue),
    options.fallbackExtendedFromLocaleWhenEmpty,
  );

export const buildExtendedFooterHtml = (
  config: FooterDisclaimerConfig,
): { disclaimer: string; secondaryDisclaimer: string } => {
  if (config.renderLinesAsPrimary) {
    const disclaimer = config.lines
      .filter((line) => line.trim().length > 0)
      .map((line) => footnoteMarkersToSupHtml(line))
      .join('<br>');
    return { disclaimer, secondaryDisclaimer: '' };
  }

  const disclaimer = footnoteMarkersToSupHtml(
    config.primaryStatementOverride ?? FooterLocale.these_statements,
  );
  const secondaryDisclaimer = config.lines
    .filter((line) => line.trim().length > 0)
    .map((line) => `<p>${footnoteMarkersToSupHtml(line)}</p>`)
    .join('');
  return { disclaimer, secondaryDisclaimer };
};
